import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { createTeam, createInvite, joinTeamByToken } from '../tareas/api';
import type { TeamInfo } from '../tareas/types';
import { getPendingInvite, clearPendingInvite } from './inviteStorage';
import { planLimitHint } from '../billing/limits';
import type { PlanLimitHint } from '../billing/limits';
import UpgradeBanner from '../billing/UpgradeBanner';

type Step = 'create' | 'invite';

export default function Onboarding() {
  const { reloadProfile, switchTeam } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('create');
  const [nombre, setNombre] = useState('');
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [limitHint, setLimitHint] = useState<PlanLimitHint | null>(null);

  // Si hay una invitación pendiente (usuario ya registrado que abrió un enlace),
  // unirse directamente en vez de crear un equipo.
  const joinedRef = useRef(false);
  useEffect(() => {
    const inv = getPendingInvite();
    if (!inv || joinedRef.current) return;
    joinedRef.current = true;
    joinTeamByToken(inv.teamId, inv.token)
      .then(async () => {
        clearPendingInvite();
        await reloadProfile();
        switchTeam(inv.teamId);
        navigate('/bandeja', { replace: true });
      })
      .catch(() => {
        // Token inválido/caducado: limpiamos y seguimos al alta normal de equipo.
        clearPendingInvite();
      });
  }, [reloadProfile, switchTeam, navigate]);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLimitHint(null);
    setBusy(true);
    try {
      const t = await createTeam(nombre);
      // El nuevo espacio pasa a ser el activo (la cabecera lo usará: el paso de
      // invitar y el resto de la app operan ya sobre él).
      switchTeam(t.id);
      await reloadProfile();
      setTeam(t);
      setStep('invite');
    } catch (err) {
      const hint = planLimitHint(err);
      if (hint) setLimitHint(hint);
      else setError(err instanceof Error ? err.message : 'No se pudo crear el equipo');
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    navigate('/bandeja', { replace: true });
  };

  if (step === 'create') {
    return (
      <Shell stepIndex={0}>
        <h1 style={styles.title}>Crea tu espacio de trabajo</h1>
        <p style={styles.subtitle}>
          Elige un nombre que tu equipo reconozca, como el de la empresa. Podrás cambiarlo cuando quieras.
        </p>
        <form onSubmit={handleCreate} style={styles.form}>
          <input
            autoFocus
            type="text"
            placeholder="Ej. Brokalia"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={80}
            required
            style={styles.input}
          />
          {error && <p style={styles.error}>{error}</p>}
          {limitHint && <UpgradeBanner hint={limitHint} />}
          <button type="submit" disabled={busy || !nombre.trim()} style={styles.primary}>
            {busy ? '...' : 'Continuar'}
          </button>
        </form>
        <p style={styles.hint}>
          ¿Tienes una invitación? Abre el enlace que te compartieron en lugar de crear un equipo.
        </p>
      </Shell>
    );
  }

  return (
    <Shell stepIndex={1}>
      <InviteStep team={team!} onFinish={finish} />
    </Shell>
  );
}

function InviteStep({ team, onFinish }: { team: TeamInfo; onFinish: () => void }) {
  const link = `${window.location.origin}/join/${team.id}/${team.join_token}`;
  const [emails, setEmails] = useState('');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [error, setError] = useState('');
  const [limitHint, setLimitHint] = useState<PlanLimitHint | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('No se pudo copiar; selecciona y copia el enlace manualmente.');
    }
  };

  const sendInvites = async () => {
    const list = emails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter((e) => e.includes('@'));
    if (list.length === 0) { onFinish(); return; }
    setError('');
    setLimitHint(null);
    setSending(true);
    try {
      for (const email of list) {
        await createInvite(email);
      }
      setSentCount(list.length);
      setTimeout(onFinish, 700);
    } catch (err) {
      const hint = planLimitHint(err);
      if (hint) setLimitHint(hint);
      else setError(err instanceof Error ? err.message : 'No se pudieron enviar las invitaciones');
      setSending(false);
    }
  };

  return (
    <>
      <h1 style={styles.title}>Invita a tu equipo</h1>
      <p style={styles.subtitle}>
        <strong>{team.nombre}</strong> es mejor en equipo. Comparte el enlace o invita por email. Puedes hacerlo también más tarde.
      </p>

      <label style={styles.label}>Enlace de invitación</label>
      <div style={styles.linkRow}>
        <input readOnly value={link} style={{ ...styles.input, flex: 1, fontSize: 13 }} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" onClick={copy} style={styles.secondary}>
          {copied ? '¡Copiado!' : 'Copiar'}
        </button>
      </div>

      <label style={{ ...styles.label, marginTop: 16 }}>Invitar por email</label>
      <textarea
        placeholder="ana@empresa.com, juan@empresa.com"
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
        rows={3}
        style={styles.textarea}
      />
      {error && <p style={styles.error}>{error}</p>}
      {limitHint && <div style={{ marginTop: 12 }}><UpgradeBanner hint={limitHint} /></div>}
      {sentCount > 0 && <p style={styles.ok}>Enviadas {sentCount} invitaciones.</p>}

      <div style={styles.actionsRow}>
        <button type="button" onClick={sendInvites} disabled={sending} style={styles.primary}>
          {sending ? 'Enviando…' : emails.trim() ? 'Enviar invitaciones' : 'Continuar'}
        </button>
        <button type="button" onClick={onFinish} style={styles.linkBtn}>
          Omitir este paso
        </button>
      </div>
    </>
  );
}

function Shell({ children, stepIndex }: { children: React.ReactNode; stepIndex: number }) {
  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        <div style={styles.steps}>
          {[0, 1].map((i) => (
            <span key={i} style={{ ...styles.stepDot, background: i <= stepIndex ? '#16a34a' : '#e2e8f0' }} />
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  screen: {
    minHeight: '100vh',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    padding: 24,
  },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '36px 36px 32px',
    width: 440,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
  },
  steps: { display: 'flex', gap: 6, marginBottom: 8 },
  stepDot: { width: 28, height: 5, borderRadius: 3 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, color: '#0f172a' },
  subtitle: { color: '#64748b', fontSize: 14, margin: '0 0 8px', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 },
  input: {
    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
    padding: '11px 14px', color: '#0f172a', fontSize: 15, outline: 'none',
  },
  textarea: {
    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
    padding: '11px 14px', color: '#0f172a', fontSize: 14, outline: 'none',
    resize: 'vertical', fontFamily: 'inherit',
  },
  linkRow: { display: 'flex', gap: 8, alignItems: 'center' },
  actionsRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 },
  primary: {
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8,
    padding: '11px 18px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  secondary: {
    background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8,
    padding: '11px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  linkBtn: { background: 'transparent', border: 'none', color: '#64748b', fontSize: 14, cursor: 'pointer', padding: 4 },
  hint: { fontSize: 12.5, color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.5 },
  error: { color: '#dc2626', fontSize: 13, margin: 0 },
  ok: { color: '#16a34a', fontSize: 13, margin: 0 },
};
